/**
 * V2 admin data layer — the loaders behind the ranger station.
 *
 * Mirrors V1's admin-dashboard.js/admin-helpers.js data access exactly:
 * the fast-count RPCs from 20260201_performance_indexes.sql first, with
 * the proven paginate-and-count fallbacks when a migration hasn't been
 * applied. All access goes through the raw REST gateway.
 */
import * as gateway from '../../core/gateway.js';
import { drainRows } from '../../core/drain.js';
import { getApiBase } from '../../core/config.js';

export const PACIFIC_TIMEZONE = 'America/Los_Angeles';

/** Start of "today" in Pacific time as a UTC ISO string (V1 admin-helpers.js). */
export function getPacificMidnightISO() {
  const now = new Date();
  const pacificDate = now.toLocaleDateString('en-CA', { timeZone: PACIFIC_TIMEZONE });
  const [year, month, day] = pacificDate.split('-').map(Number);
  const pacificMidnight = new Date(Date.UTC(year, month - 1, day));
  const testDate = new Date(year, month - 1, day, 12, 0, 0);
  const utcDate = new Date(testDate.toLocaleString('en-US', { timeZone: 'UTC' }));
  const pacificDateObj = new Date(testDate.toLocaleString('en-US', { timeZone: PACIFIC_TIMEZONE }));
  const offsetMs = utcDate - pacificDateObj;
  return new Date(pacificMidnight.getTime() + offsetMs).toISOString();
}

/** Count rows by paginating ids — works without any count RPC. */
export async function countAll(table, filters = {}, pageSize = 1000) {
  let total = 0;
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await gateway.rawQuery(table, {
      select: 'id', filters, limit: pageSize, offset,
    });
    if (error) throw error;
    const count = data?.length || 0;
    total += count;
    offset += pageSize;
    hasMore = count === pageSize;
  }
  return total;
}

/** Fetch every row of a query, page by page. */
export async function fetchAll(table, options = {}, pageSize = 1000) {
  const all = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const { data, error } = await gateway.rawQuery(table, {
      ...options, limit: pageSize, offset,
    });
    if (error) throw error;
    const records = data || [];
    all.push(...records);
    offset += pageSize;
    hasMore = records.length === pageSize;
  }
  return all;
}

// ---------------------------------------------------------------------------
// Overview stats (shared by the dashboard and the shell's tab cards)
// ---------------------------------------------------------------------------

let overview = null;

export function getOverview() {
  return overview;
}

export function resetOverview() {
  overview = null;
}

export async function loadOverview() {
  try {
    const pacificMidnight = getPacificMidnightISO();
    const [yoinkCountResult, todayCountResult] = await Promise.allSettled([
      gateway.rpc('get_yoink_count_fast'),
      gateway.rpc('get_today_yoink_count'),
    ]);
    const fastCounts =
      yoinkCountResult.status === 'fulfilled' && !yoinkCountResult.value.error;

    const [totalUsers, totalLocations, totalCategories, loggedInToday] = await Promise.all([
      countAll('public_profiles'),
      countAll('locations'),
      countAll('categories'),
      countAll(gateway.TABLES.PROFILES, { last_login: `gte.${pacificMidnight}` }),
    ]);

    const [totalYoinks, todayYoinks] = fastCounts
      ? [
          yoinkCountResult.value.data || 0,
          todayCountResult.status === 'fulfilled' ? todayCountResult.value.data || 0 : 0,
        ]
      : await Promise.all([
          countAll('yoinks'),
          countAll('yoinks', { created_at: `gte.${pacificMidnight}` }),
        ]);

    // Follow-ups owed today or earlier, for the Marketing tab's badge. Its
    // own failure is never the overview's: the table doesn't exist until
    // 20260814_marketing_prospects.sql is run by hand, and a missing badge
    // beats a missing dashboard.
    const marketingDue = await countAll(gateway.TABLES.MARKETING_PROSPECTS, {
      next_followup: `lte.${new Date().toLocaleDateString('en-CA')}`,
      status: 'in.(prospect,list_built,pitched,replied,claimed)',
    }).catch(() => 0);

    // Unanswered Tell Us messages, for the Feedback tab's badge. Same
    // deal as marketingDue: the table doesn't exist until
    // 20260829_feedback.sql is run by hand, so its failure is a missing
    // badge, never a missing dashboard.
    const feedbackOpen = await countAll(gateway.TABLES.FEEDBACK, {
      status: 'eq.new',
    }).catch(() => 0);

    overview = {
      totalYoinks, totalUsers, totalLocations, totalCategories, todayYoinks, loggedInToday,
      marketingDue, feedbackOpen,
    };
  } catch {
    overview = null;
  }
  return overview;
}

// ---------------------------------------------------------------------------
// Dashboard loaders (each fails soft to keep the rest of the page alive)
// ---------------------------------------------------------------------------

/** Top users by yoink count — RPC with client-side fallback (V1 parity). */
export async function loadTopUsers() {
  try {
    const { data, error } = await gateway.rpc('get_top_users_by_yoinks', { limit_count: 5 });
    if (!error && data) {
      return data.map((row) => ({
        userId: row.user_id,
        name: row.full_name || row.username || 'Unknown',
        username: row.username || null,
        count: row.yoink_count,
      }));
    }
    const all = await fetchAll('yoinks', { select: 'user_id,public_profiles(full_name,username)' });
    const counts = {};
    const names = {};
    const usernames = {};
    all.forEach((y) => {
      if (!y.user_id) return;
      counts[y.user_id] = (counts[y.user_id] || 0) + 1;
      if (y.public_profiles) {
        names[y.user_id] = y.public_profiles.full_name || y.public_profiles.username || 'Unknown';
        usernames[y.user_id] = y.public_profiles.username || null;
      }
    });
    return Object.entries(counts)
      .map(([userId, count]) => ({
        userId, count, name: names[userId] || 'Unknown', username: usernames[userId] || null,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  } catch {
    return [];
  }
}

export async function loadTopCategories() {
  try {
    const { data, error } = await gateway.rpc('get_top_categories_by_yoinks', { limit_count: 5 });
    if (!error && data) {
      return data.map((row) => ({
        catId: row.category_id,
        name: row.category_name || 'Unknown',
        emoji: row.category_emoji || '📍',
        count: row.yoink_count,
      }));
    }
    const all = await fetchAll('yoinks', { select: 'category_id,categories:category_id(name,emoji)' });
    const counts = {};
    const meta = {};
    all.forEach((y) => {
      if (!y.category_id) return;
      counts[y.category_id] = (counts[y.category_id] || 0) + 1;
      if (y.categories) meta[y.category_id] = { name: y.categories.name, emoji: y.categories.emoji || '📍' };
    });
    return Object.entries(counts)
      .map(([catId, count]) => ({
        catId, count, name: meta[catId]?.name || 'Unknown', emoji: meta[catId]?.emoji || '📍',
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  } catch {
    return [];
  }
}

export async function loadTopLocations() {
  try {
    const { data, error } = await gateway.rpc('get_top_locations_by_yoinks', { limit_count: 5 });
    if (!error && data) {
      return data.map((row) => ({
        locId: row.location_id,
        name: row.location_name || 'Unknown',
        count: row.yoink_count,
      }));
    }
    const all = await fetchAll('yoinks', { select: 'location_id,locations:location_id(display_name)' });
    const counts = {};
    const names = {};
    all.forEach((y) => {
      if (!y.location_id) return;
      counts[y.location_id] = (counts[y.location_id] || 0) + 1;
      if (y.locations) names[y.location_id] = y.locations.display_name;
    });
    return Object.entries(counts)
      .map(([locId, count]) => ({ locId, count, name: names[locId] || 'Unknown' }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  } catch {
    return [];
  }
}

/** A Date's Pacific calendar date as YYYY-MM-DD ('en-CA' formats ISO-style). */
export function pacificDayKey(date = new Date()) {
  return date.toLocaleDateString('en-CA', { timeZone: PACIFIC_TIMEZONE });
}

/**
 * The last `n` Pacific calendar days, oldest first, ending with today in
 * Pacific.
 *
 * Anchored at noon UTC on the Pacific date and stepped in whole days, so
 * neither the viewer's own timezone nor a DST shift can skip a day or
 * mislabel one. The lastNDays() this replaces built its `date` keys from the
 * DEVICE's midnight (via toISOString, i.e. a UTC date) but its `label` from
 * Pacific — the two disagree for any device east of Pacific, which put the
 * weekday names on the wrong columns.
 */
export function pacificWindow(n) {
  const [y, m, d] = pacificDayKey().split('-').map(Number);
  const anchor = Date.UTC(y, m - 1, d, 12, 0, 0);
  const days = [];
  for (let i = n - 1; i >= 0; i--) {
    const day = new Date(anchor - i * 86400000);
    days.push({
      date: day.toISOString().slice(0, 10),
      label: day.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    });
  }
  return days;
}

/** Index RPC rows as { 'YYYY-MM-DD' => count }, tolerating string counts. */
function countsByDay(rows, dateKey, countKey) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = String(row?.[dateKey] ?? '').slice(0, 10);
    if (key) map.set(key, Number(row[countKey]) || 0);
  });
  return map;
}

/**
 * "When the row landed" is `inserted_at` (migration 20260915b), NOT
 * `created_at` — the reviews importer sets created_at to the original visit
 * date (reviews-page.js:498), so a 246-yoink import reads as 2022-2026
 * history rather than as today's activity.
 *
 * Migrations here are applied by hand, so the column may not exist yet, and
 * selecting a column that is not there 400s the whole query. Probe once per
 * session and remember the answer — same shape as queryPublicYoinks'
 * personal_category_id fallback in src/features/journal/journal.js.
 */
let insertedAtMissing = false;
async function scanActivityRows(from) {
  if (!insertedAtMissing) {
    const rows = await fetchAll('yoinks', {
      select: 'inserted_at',
      filters: { inserted_at: `gte.${from.toISOString()}` },
      order: 'inserted_at.asc,id.asc',
    }).catch((error) => {
      if (!/HTTP 400/.test(error?.message || '')) throw error;
      return null;
    });
    if (rows) return { rows, column: 'inserted_at' };
    insertedAtMissing = true;
  }
  const rows = await fetchAll('yoinks', {
    select: 'created_at',
    filters: { created_at: `gte.${from.toISOString()}` },
    order: 'created_at.asc,id.asc',
  });
  return { rows, column: 'created_at' };
}

/**
 * Yoinks per Pacific day, oldest first (RPC first, raw scan fallback).
 *
 * The CLIENT owns the window and the server owns the counts. That split is
 * deliberate: before 20260915 the RPC anchored its series on CURRENT_DATE,
 * which resolves in the session timezone (UTC on Supabase) while the rows
 * were bucketed by Pacific date — so after 5pm Pacific it returned a phantom
 * trailing bucket for "UTC tomorrow" that could not hold a row, and dropped a
 * real day off the front. Projecting the RPC's rows onto a window we compute
 * here drops the phantom and keeps the weekday labels on the right columns
 * whether or not the migration has been applied by hand yet.
 *
 * We ask for one day MORE than we plot so the un-migrated function's shifted
 * window still covers ours; once the migration lands the extra day is simply
 * unused.
 */
export async function loadActivitySeries(days = 14) {
  const windowDays = pacificWindow(days);
  try {
    const { data, error } = await gateway.rpc('get_activity_chart_data', { days: days + 1 });
    if (!error && Array.isArray(data) && data.length) {
      const counts = countsByDay(data, 'activity_date', 'yoink_count');
      return windowDays.map((d) => ({ ...d, count: counts.get(d.date) || 0 }));
    }
    // Widen by a day so the Pacific bucketing below can never be clipped by a
    // UTC-midnight filter boundary; the bucketing itself stays exact.
    const from = new Date(Date.parse(`${windowDays[0].date}T00:00:00Z`) - 86400000);
    const { rows: all, column } = await scanActivityRows(from);
    const perDay = new Map();
    all.forEach((y) => {
      const at = y?.[column];
      if (!at) return;
      const key = pacificDayKey(new Date(at));
      perDay.set(key, (perDay.get(key) || 0) + 1);
    });
    return windowDays.map((d) => ({ ...d, count: perDay.get(d.date) || 0 }));
  } catch {
    return [];
  }
}

/**
 * Unique signed-in users per Pacific day — the second line on the activity
 * chart, so it MUST share loadActivitySeries' window or the two series sit on
 * different days while sharing one x-axis.
 */
export async function loadLoginSeries(days = 14) {
  const windowDays = pacificWindow(days);
  try {
    const { data, error } = await gateway.rpc('get_login_activity_14_days');
    if (error) {
      // Migration not applied yet — chart shows zeros rather than dying
      return windowDays.map((d) => ({ ...d, count: 0 }));
    }
    const counts = countsByDay(data, 'login_date', 'unique_users');
    return windowDays.map((d) => ({ ...d, count: counts.get(d.date) || 0 }));
  } catch {
    return windowDays.map((d) => ({ ...d, count: 0 }));
  }
}

export async function loadRecentYoinks() {
  try {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
      select: 'id,title,images,created_at,public_profiles(full_name,username),categories:category_id(name,emoji)',
      order: 'created_at.desc',
      limit: 10,
    });
    if (error) throw error;
    return data || [];
  } catch {
    return [];
  }
}

/** Ids that must not count as explorers: seeded preview accounts. */
async function previewUserIds() {
  const rows = await drainRows(gateway.TABLES.PROFILES, {
    select: 'id,is_preview,role',
    uniqueKey: 'id',
  });
  return new Set(
    rows.filter((p) => p.is_preview === true || p.role === 'preview').map((p) => p.id)
  );
}

/**
 * Week-over-week active explorers from login_history.
 *
 * REWRITTEN 2026-07-25. The previous version was wrong three ways at once,
 * and it is the number the dashboard invites you to steer by:
 *
 *   1. It compared a week-TO-DATE against a full prior week — the window
 *      started at the most recent Sunday, so on a Monday it was one day
 *      against seven and the rate read catastrophically low. It was only
 *      honest on Saturdays. Now both windows are a rolling 7 days, so the
 *      comparison is like-for-like on any day.
 *   2. Neither query passed a `limit`, so PostgREST capped each at 1,000 rows
 *      (supabase-gateway.js only appends &limit= when one is given).
 *      login_history gets a row per login, so the cap was plausibly already
 *      truncating — silently, since a cap is not an error. Both reads now
 *      drain in keyset batches.
 *   3. It counted seeded preview accounts, which users.js excludes from every
 *      other roster figure, inflating both weeks.
 *
 * Also renamed the misleading field: what used to be `newThisWeek` was
 * "active now, not active last week", which is mostly RETURNING lapsed users,
 * not new signups. It is `resurfaced` now.
 *
 * Still measures LOGINS, not logging. That is the honest name for it — see
 * docs/THE-NUMBER.md, where weekly *loggers* is the metric that actually
 * decides the roadmap, read via docs/sql/read-the-number.sql.
 */
export async function loadRetention() {
  try {
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const sevenAgo = new Date(now - 7 * DAY).toISOString();
    const fourteenAgo = new Date(now - 14 * DAY).toISOString();

    const [recent, preview] = await Promise.all([
      drainRows(gateway.TABLES.LOGIN_HISTORY, {
        select: 'id,user_id,logged_in_at',
        filters: { logged_in_at: `gte.${fourteenAgo}` },
        uniqueKey: 'id',
      }),
      previewUserIds(),
    ]);

    const thisWeekUsers = new Set();
    const lastWeekUsers = new Set();
    for (const row of recent) {
      if (!row.user_id || preview.has(row.user_id)) continue;
      if (row.logged_in_at >= sevenAgo) thisWeekUsers.add(row.user_id);
      else lastWeekUsers.add(row.user_id);
    }

    const retained = [...thisWeekUsers].filter((u) => lastWeekUsers.has(u)).length;
    return {
      thisWeekActive: thisWeekUsers.size,
      lastWeekActive: lastWeekUsers.size,
      retained,
      lapsed: lastWeekUsers.size - retained,
      resurfaced: thisWeekUsers.size - retained,
      retentionRate: lastWeekUsers.size > 0 ? Math.round((retained / lastWeekUsers.size) * 100) : 0,
    };
  } catch {
    return null;
  }
}

/** Per-category yoinks this week vs last (V1 parity). */
export async function loadCategoryHealth() {
  try {
    const now = Date.now();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now - 14 * 24 * 60 * 60 * 1000);

    const { data: categories, error: catErr } = await gateway.rawQuery(gateway.TABLES.CATEGORIES, {
      select: 'id,name,emoji', order: 'name.asc',
    });
    if (catErr) throw catErr;

    const [thisWeekResult, lastWeekResult] = await Promise.all([
      gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'category_id',
        filters: { created_at: `gte.${oneWeekAgo.toISOString()}` },
      }),
      gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'category_id',
        filters: {
          created_at: `gte.${twoWeeksAgo.toISOString()}`,
          and: `(created_at.lt.${oneWeekAgo.toISOString()})`,
        },
      }),
    ]);
    if (thisWeekResult.error || lastWeekResult.error) throw thisWeekResult.error || lastWeekResult.error;

    const thisCounts = {};
    const lastCounts = {};
    (thisWeekResult.data || []).forEach((y) => {
      thisCounts[y.category_id] = (thisCounts[y.category_id] || 0) + 1;
    });
    (lastWeekResult.data || []).forEach((y) => {
      lastCounts[y.category_id] = (lastCounts[y.category_id] || 0) + 1;
    });

    return (categories || [])
      .map((cat) => {
        const thisWeek = thisCounts[cat.id] || 0;
        const lastWeek = lastCounts[cat.id] || 0;
        const change = thisWeek - lastWeek;
        return {
          id: cat.id,
          name: cat.name,
          emoji: cat.emoji || '📁',
          thisWeek,
          lastWeek,
          change,
          trend: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
        };
      })
      .sort((a, b) => b.thisWeek - a.thisWeek);
  } catch {
    return [];
  }
}

/** Google proxy usage stats for the API health widget. */
export async function loadApiUsage() {
  try {
    // usage-stats is admin-gated server-side (it leaks traffic/cost data),
    // so send the Supabase admin JWT the rest of the console uses.
    const session = await gateway.getSession();
    const token = session?.data?.session?.access_token;
    if (!token) return { error: 'Not authenticated' };
    // Cache-busted: a fixed-URL GET of mutable state is what a browser or
    // page cache serves stale, and stale counters here read as real ones.
    // See docs/BEST-PRACTICES.md §17b.
    const response = await fetch(`${getApiBase()}/google-proxy.php?action=usage-stats&t=${Date.now()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!response.ok) return { error: `HTTP ${response.status}` };
    const text = await response.text();
    try {
      const data = JSON.parse(text);
      return data.temperature !== undefined ? data : { error: 'Invalid response format' };
    } catch {
      return { error: 'Parse error', details: text.substring(0, 100) };
    }
  } catch (err) {
    return { error: err.message };
  }
}
